import { AxiosResponse } from 'axios';

import { QueryObserverOptions, useQueryClient } from '@tanstack/react-query';
import { toastService } from '@thefarmersfront/kitchen-mobile';

import { useCodesStore } from '../../store';
import { API_VERSION_V2 } from '~/constants';
import { axiosInstance } from '~/services/fetchServiceV2';
import { TypeBankCodeState } from '~/store/codes/useCodesStore';
import { TypeErrorFormat, requestHelper } from '~/utils/fetchHelper';

export type TypeGetBankCodesResponse = {
  code: string;
  message: string;
  data: { banks: TypeBankCodeState[]; securities: TypeBankCodeState[] };
};

export const useGetBankCodes = () => {
  const queryClient = useQueryClient();

  const { setBankCodes } = useCodesStore((state) => state.actions);

  const handleSuccess = (response: TypeGetBankCodesResponse) => {
    const { banks, securities } = response.data || {};

    const bankCodes =
      banks?.map((bank) => ({
        ...bank,
        type: 'BANK' as const,
      })) ?? [];

    const securityCodes =
      securities?.map((security) => ({
        ...security,
        type: 'SECURITY' as const,
      })) ?? [];

    setBankCodes([...bankCodes, ...securityCodes]);
  };

  const handleError = (error: TypeErrorFormat) => {
    toastService.addToast({ message: error.message });
    setBankCodes(null);
  };

  const requestGetBankCodes = async ({
    queryConfig,
  }: {
    queryConfig?: Omit<QueryObserverOptions<AxiosResponse<TypeGetBankCodesResponse>>, 'queryKey' | 'queryFn'>;
  } = {}) => {
    const endPoint = '/codes/financial-institution';

    const query = queryClient.ensureQueryData<AxiosResponse<TypeGetBankCodesResponse>>({
      queryKey: ['codes', 'financial-institution'],
      queryFn: () => axiosInstance.get<TypeGetBankCodesResponse>(endPoint, { headers: { version: API_VERSION_V2 } }),
      ...queryConfig,
      gcTime: 0,
    });

    return requestHelper(() => query, handleSuccess, handleError);
  };

  return requestGetBankCodes;
};
